// Composable for fetching the current Application by route param, with description artefact.
import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useEntityNameManager } from "@/composables/entity-name-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { clearStaleContentOnSwitch } from "@/composables/stale-while-revalidate";
import { useStekManager } from "@/composables/stek-manager";
import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type Application = types.Application;

// Fetches the Application identified by `application.value.identity` from the current entity context.
export const useApplicationStore = defineEntityStore("application", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const syncStore = useSyncStore();
    const { application: applicationInfo } = useCurrentEntityManager();
    const entityId = computed(() => application.value?.identity);

    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.APPLICATION,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(
        entityId,
        [descriptionDraft],
        EntityType.APPLICATION
    );

    const isFetching = ref(false);
    const isFetchingDescription = ref(false);
    const application: Ref<Application | undefined> = ref(undefined);
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(application);
    // Latest-request guard: each fetchApplication gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();

    async function fetchApplication(force = false) {
        const applicationId = applicationInfo.value?.identity;

        if (
            !force &&
            application.value &&
            application.value.identity === applicationId
        ) {
            return;
        }

        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = begin();

        if (!applicationId) {
            description.value = undefined;
            application.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded application +
        // description on screen while the *same* application is refetched, so it
        // doesn't blank the page. Switching to a *different* application is
        // handled by clearStaleContentOnSwitch below.

        try {
            const { response, data } =
                await api.startDevApplicationMgtGetApplicationStartPost({
                    body: { application_id: applicationId },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value!.name,
                            stekName: stek.value!.name,
                        },
                        query: { type: "application", id: applicationId },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-application refetch is dropped; navigating
            // to a different application must always commit. A dropped
            // commit self-heals: the edit's own sync re-notifies and
            // refetches.
            if (
                applicationId === application.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            application.value = data;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-application refetch
            // would read as a local edit (the name manager only re-baselines
            // on identity change) and get echoed back as a save.
            persistName();

            if (application.value) {
                await fetchDescription(signal, token);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }

    // Drop the previous application's content when the route switches to a
    // different application, so stale-while-revalidate never renders the old one
    // on the new page.
    clearStaleContentOnSwitch(
        () => applicationInfo.value?.identity,
        [application, description]
    );
    watch(
        () => applicationInfo.value?.identity,
        () => fetchApplication(),
        { immediate: true }
    );

    const fetchDescription = async (signal: AbortSignal, token: number) => {
        // Always refetch for the (possibly new) current application — under SWR
        // the previous description stays set, so a `!description.value` guard
        // would wrongly keep the stale value.
        if (application.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    application.value,
                    "applicationDescription",
                    signal
                );

                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                if (isCurrent(token)) {
                    isFetchingDescription.value = false;
                }
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() =>
        Boolean(isNameModified.value || description.value?.isModified)
    );

    // One shared opts object: the notification guards and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.APPLICATION,
        currentEntityId: () => application.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => fetchApplication(true),
    };

    refreshOnExternalEntityChange(externalRefresh);

    const queueSave = async () => {
        if (!isModified.value || !application.value) return;

        const updateContent = {
            body: {
                application_id: application.value.identity,
            } as Record<string, unknown>,
            url: "/dev/application_mgt/update_application/start",
        };
        if (isNameModified.value) {
            updateContent.body.name = application.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.application_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.APPLICATION,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) throttledSave();
    });

    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    !stek.value ||
                    !fullySyncedIdentities.some(
                        (identity) =>
                            identity.entityType === EntityType.APPLICATION &&
                            identity.entityId === application.value?.identity
                    )
                ) {
                    return;
                }

                void fetchApplication(true);
            });
        }
    });

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    const linkProcessTask = async (processTaskId: string) => {
        if (!application.value) return;
        await api.startDevApplicationMgtLinkProcessTaskToApplicationStartPost({
            body: {
                application_id: application.value.identity,
                process_task_id: processTaskId,
            },
        });
        await fetchApplication(true);
    };

    const unlinkProcessTask = async (processTaskId: string) => {
        if (!application.value) return;
        await api.startDevApplicationMgtUnlinkProcessTaskFromApplicationStartPost(
            {
                body: {
                    application_id: application.value.identity,
                    process_task_id: processTaskId,
                },
            }
        );
        await fetchApplication(true);
    };

    return {
        application,
        description,
        isFetching: computed(
            () => isFetching.value || isFetchingDescription.value
        ),
        isModified,
        reset,
        linkProcessTask,
        unlinkProcessTask,
    };
});

export const useApplicationManager = () => storeToRefs(useApplicationStore());
